import { useEffect, useMemo, useState } from 'react'
import { FiltersPanel } from './FiltersPanel'
import { ShortProfileResults } from './ShortProfileResults'
import { SelectField } from 'cwp-components/forms'
import { FilterButton } from './FilterButton'
import { useSearchParams } from 'react-router-dom'
import { useGetShortProfiles } from 'common/hooks/useGetShortProfiles'
import { SortByIcon } from './SortByIcon'
import { ClearFiltersButton } from './ClearFiltersButton'
import debounce from 'lodash/debounce'
import LoadingSpinner from 'cwp-components/base/LoadingSpinner'

export const SORT_OPTIONS = [
  {
    label: 'Price (hourly)',
    value: 'price',
    sort: 'price',
    order: 'ASC',
  },
  {
    label: 'Location radius',
    value: 'radius',
    sort: 'radius',
    order: 'ASC',
  },
  {
    label: 'Regulated (years)',
    value: 'regulated_years',
    sort: 'regulated_years',
    order: 'DESC',
  },
  {
    label: 'Customer satisfaction',
    value: 'rating',
    sort: 'rating',
    order: 'DESC',
  },
]

const FindALawyerIntelligenceSearch = () => {
  const {
    loadShortProfiles,
    shortProfiles,
    loadMoreShortProfiles,
    hasMoreShortProfiles,
    metaData,
    totalShortProfiles,
    isShortProfilesLoading,
    isMoreLoading,
    setIsMoreLoading,
    shortProfilePage
  } = useGetShortProfiles('proSearch')

  const [searchParams, setSearchParams] = useSearchParams()
  const [filtersVisible, setFiltersVisible] = useState(false)

  const onSortChange = (selectedValue: string) => {
    const selected = SORT_OPTIONS.find((o) => o.value === selectedValue)
    if (selected) {
      searchParams.set('sort', selected.value)
      setSearchParams(searchParams)
    }
  }

  const debouncedLoad = useMemo(
    () => debounce((query: string) => loadShortProfiles(query, 10), 300),
    [loadShortProfiles],
  )

  useEffect(() => {
    debouncedLoad(searchParams.toString())
  }, [searchParams.toString(), debouncedLoad])

  const handleSeeMore = () => {
    setIsMoreLoading(true)
    loadMoreShortProfiles(10)
  }

  return (
    <>
      <div className='flex h-[80px] md:h-[102px] bg-gradient-to-r from-[#CCE0DD] from-0% via-[#9FB7D7] via-50% to-[rgba(190,190,223,0.98)] to-100%'>
        <p className='m-auto md:text-[32px] text-lg text-white font-bold'>
          Find a Lawyer
        </p>
      </div>
      {isShortProfilesLoading ? (
        <div className='min-h-[500px] my-4 h-full sm:mx-10 text-center pt-10'>
          <h1 className='text-[#405985] text-lg font-bold mb-8'>
            Searching lawyer profiles...
          </h1>
          <LoadingSpinner />
        </div>
      ) : (
        <div className='!px-5 lg:!px-10 xl:!px-20 !pt-4 relative'>
          <div className='gap-3 md:mb-12'>
            <div className='flex w-full items-center gap-4 md:gap-6'>
              <FilterButton onClick={() => setFiltersVisible(true)} />
              {!filtersVisible && (
                <SelectField
                  required={false}
                  id='SortBy'
                  label='Sort By'
                  placeholder='Sort By'
                  records={SORT_OPTIONS}
                  selectClassName='!h-[40px] sm:min-w-36 w-full'
                  value={
                    SORT_OPTIONS.find((o) => o.value === searchParams.get('sort'))
                      ?.label
                  }
                  onChange={(e) => onSortChange(e.target.value)}
                  mobileIcon={<SortByIcon />}
                />
              )}
              <ClearFiltersButton />
              <div className='flex md:hidden flex-1 justify-end'>
                <div className='justify-self-end text-xs font-medium text-[#001A3D]'>
                  {totalShortProfiles}{' '}
                  {totalShortProfiles === 1 ? 'result' : 'results'}
                </div>
              </div>
            </div>
          </div>
          {filtersVisible && (
            <FiltersPanel
              {...{
                setFiltersVisible,
                metaData,
              }}
            />
          )}
          <ShortProfileResults
            {...{
              shortProfiles,
              hasMoreShortProfiles,
              handleSeeMore,
              totalShortProfiles,
              isMoreLoading,
              shortProfilePage
            }}
          />
        </div>
      )}
    </>

  )
}

export default FindALawyerIntelligenceSearch
